import { App } from "@capacitor/app"
import { Capacitor } from "@capacitor/core"

type VisibilityListener = (active: boolean) => void

let appVisible = typeof document === "undefined" ? true : document.visibilityState !== "hidden"

export function isAppVisible(): boolean {
  return appVisible
}

export function watchAppVisibility(listener: VisibilityListener): () => void {
  let removed = false
  let nativeHandle: { remove: () => Promise<void> } | undefined
  const setVisible = (active: boolean) => {
    appVisible = active
    listener(active)
  }
  const onVisibilityChange = () => setVisible(document.visibilityState !== "hidden")

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibilityChange)
  }
  if (Capacitor.isNativePlatform()) {
    void App.addListener("appStateChange", ({ isActive }) => setVisible(isActive)).then((handle) => {
      if (removed) void handle.remove()
      else nativeHandle = handle
    })
  }

  listener(appVisible)
  return () => {
    removed = true
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
    void nativeHandle?.remove()
  }
}

export async function requestMatchAlertPermission(): Promise<void> {
  if (!("Notification" in window) || Notification.permission !== "default") return
  try {
    await Notification.requestPermission()
  } catch {
    // Browser notification permission is optional.
  }
}

export async function notifyMatchFound(title: string, body: string): Promise<void> {
  try {
    navigator.vibrate?.([40, 60, 40])
  } catch {
    // Haptics are optional and unsupported on many browsers.
  }
  if (isAppVisible() || !("Notification" in window)) return
  try {
    const permission = Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission
    if (permission === "granted") {
      new Notification(title, { body, tag: "joowfak-match" })
    }
  } catch {
    // Background alerts are best-effort without APNs/CallKit.
  }
}
